import { Controller } from "@hotwired/stimulus";
import { applyAddOnToScenarioTotals } from "../scenario_modeling/contribution";
import {
  dispatchModelingPayload,
  hideModelingLoading,
  showModelingLoading,
} from "../scenario_modeling/payload_refresh";
import { appendModelingControlParams } from "../scenario_modeling/request_params";

const DISPLAY_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default class extends Controller {
  static targets = [
    "currencyToggle",
    "currencyDisplayInput",
    "exchangeRateDateDisplay",
    "exchangeRateDateInput",
  ];

  static values = {
    currencyDisplay: { type: String, default: "payment" },
    exchangeRatesUrl: String,
  };

  connect() {
    if (this.currencyDisplayValue === "local") {
      this.currencyDisplayValue = "facility";
    }
    this._syncCurrencyToggles();
    this.syncExchangeRateDateDisplay();
    this._fetchInFlight = false;
    this._topUpChangeHandler = this.toggleTopUp.bind(this);
    this.element.addEventListener("change", this._topUpChangeHandler);
  }

  disconnect() {
    if (this._topUpChangeHandler) {
      this.element.removeEventListener("change", this._topUpChangeHandler);
    }
  }

  onGapTargetInput(event) {
    window.dispatchEvent(
      new CustomEvent("scenario-modeling:gap-target-change", {
        detail: { value: event.target.value },
      })
    );
  }

  toggleTopUp(event) {
    const checkbox = event?.target;
    if (!checkbox || checkbox.id !== "scenario_top_up_enabled") return;

    const section = document.getElementById("scenario-top-up-section");
    if (!section) return;

    section.classList.toggle("hidden", !checkbox.checked);
    applyAddOnToScenarioTotals();
    window.dispatchEvent(new CustomEvent("scenario-modeling:top-up-change"));
  }

  selectCurrency(event) {
    const rawValue = event.currentTarget.dataset.currencyDisplay;
    const value = rawValue === "local" ? "facility" : rawValue;
    if (!value || value === this.currencyDisplayValue || this._fetchInFlight) return;

    const previous = this.currencyDisplayValue;

    if (value === "facility") {
      this.currencyDisplayValue = "facility";
      this._syncCurrencyToggles();

      window.dispatchEvent(
        new CustomEvent("scenario-modeling:currency-display-change", {
          detail: { value: "facility" },
        })
      );
      return;
    }

    this.currencyDisplayValue = value;
    this._syncCurrencyToggles();
    this._fetchExchangeRates(value, previous);
  }

  openDatePicker(event) {
    event.preventDefault();
    this._showDatePicker();
  }

  openDatePickerOnKey(event) {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    this._showDatePicker();
  }

  syncExchangeRateDateDisplay() {
    if (!this.hasExchangeRateDateInputTarget || !this.hasExchangeRateDateDisplayTarget) {
      return;
    }

    const formatted = this._formatDisplayDate(this.exchangeRateDateInputTarget.value);
    if (!formatted) return;

    this.exchangeRateDateDisplayTarget.textContent = formatted;
  }

  onExchangeRateDateChange() {
    this.syncExchangeRateDateDisplay();

    if (this._fetchInFlight) return;
    if (!this.hasExchangeRateDateInputTarget || !this.exchangeRateDateInputTarget.value) return;

    const previous = this.currencyDisplayValue;
    this.currencyDisplayValue = "payment";
    this._syncCurrencyToggles();

    this._fetchExchangeRates("payment", previous);
    window.dispatchEvent(new CustomEvent("scenario-modeling:exchange-rate-date-change"));
  }

  async _fetchExchangeRates(currencyDisplay, previousDisplay) {
    if (!this.exchangeRatesUrlValue) return;

    this._fetchInFlight = true;
    this._setCurrencyTogglesDisabled(true);
    showModelingLoading();
    window.dispatchEvent(new CustomEvent("scenario-modeling:exchange-rates-loading"));

    let payload = null;

    try {
      const url = new URL(this.exchangeRatesUrlValue, window.location.origin);
      const params = new URLSearchParams();
      appendModelingControlParams(params);
      url.search = params.toString();

      const response = await fetch(url, {
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      });

      if (!response.ok) throw new Error("exchange rates request failed");

      payload = await response.json();
    } catch (_error) {
      this.currencyDisplayValue = previousDisplay;
      this._syncCurrencyToggles();
      window.dispatchEvent(new CustomEvent("scenario-modeling:exchange-rates-error"));
    } finally {
      this._fetchInFlight = false;
      this._setCurrencyTogglesDisabled(false);
      hideModelingLoading();
      window.dispatchEvent(new CustomEvent("scenario-modeling:exchange-rates-ready"));
    }

    // Apply after hideModelingLoading so restored placeholders are replaced by fresh values.
    if (!payload) return;

    dispatchModelingPayload(payload, { currencyDisplay });

    window.dispatchEvent(
      new CustomEvent("scenario-modeling:exchange-rates-loaded", {
        detail: { payload, currencyDisplay },
      })
    );
    window.dispatchEvent(
      new CustomEvent("scenario-modeling:currency-display-change", {
        detail: { value: currencyDisplay },
      })
    );
  }

  _showDatePicker() {
    if (!this.hasExchangeRateDateInputTarget) return;

    const input = this.exchangeRateDateInputTarget;
    if (typeof input.showPicker === "function") {
      try {
        input.showPicker();
        return;
      } catch (_error) {
        // Safari can throw when showPicker is not user-gesture initiated.
      }
    }

    input.focus();
    input.click();
  }

  _formatDisplayDate(isoValue) {
    if (!isoValue) return null;

    const [year, month, day] = isoValue.split("-").map((part) => Number(part));
    const monthLabel = DISPLAY_MONTHS[month - 1];
    if (!monthLabel) return null;

    return `${String(day).padStart(2, "0")} ${monthLabel} ${year}`;
  }

  _syncCurrencyToggles() {
    this.currencyToggleTargets.forEach((button) => {
      const buttonValue = button.dataset.currencyDisplay === "local" ? "facility" : button.dataset.currencyDisplay;
      const active = buttonValue === this.currencyDisplayValue;
      button.setAttribute("aria-pressed", active ? "true" : "false");
    });

    if (this.hasCurrencyDisplayInputTarget) {
      this.currencyDisplayInputTarget.value = this.currencyDisplayValue;
    }
  }

  _setCurrencyTogglesDisabled(disabled) {
    this.currencyToggleTargets.forEach((button) => {
      button.disabled = disabled;
    });
  }
}
